<template>
    <section class="media-map">
        <div class="loader-wrapper" v-if="loading">
            <Loader />
        </div>

        <div class="box-wrapper" v-show="!loading">
            <div class="box">
                <div class="title">{{ title }}</div>
                <div class="subtitle">{{ subtitle }}</div>
                <div class="row featured-localizations">
                    <Localization
                        v-for="localization of backendData.media_map_featured_localizations"
                        :key="localization.name"
                        :localization="localization"
                        :active="
                            activeTitle && activeTitle === localization.title
                        "
                        v-on:selected="changeActiveLocalization(localization)"
                    />
                </div>
                <a class="cta" :href="cta.url" v-if="cta.isVisible">{{
                    cta.label
                }}</a>
            </div>
        </div>

        <div class="map-wrapper" v-bind:style="{ opacity: loading ? 0.5 : 1 }">
            <div class="map" ref="map"></div>
        </div>
    </section>
</template>

<script>
import Localization from "./Localization";
import { WARSAW, GOOGLE_MAP_STYLES, PIN, GROUPS } from "../shared/constants";
import gmapsInit from "../shared/google-maps";
import slugify from "slugify";
import Loader from "../shared/components/Loader";
import {
    getLocalizations,
    getPublicTransport
} from "../shared/wp-ajax.service";
import {
    getActiveMarker,
    getAveragePosition,
    getDefaultMarker,
    getMarker
} from "../shared/utils";
import { all, spread } from "axios";

export default {
    name: "HomepageMap",
    components: {
        Localization,
        Loader
    },
    data() {
        return {
            center: WARSAW,
            zoom: 14,
            projects: [],
            markers: [],
            styles: GOOGLE_MAP_STYLES,
            map: undefined,
            google: undefined,
            bounds: undefined,
            mapOffset: 0, // INFO: if we have overlay, then "center" should be shifted
            mobile: false,
            markerCluster: undefined,
            isMapDragged: false,
            pristine: true,
            filters: [],
            categories: [],
            loading: true,
            activeLocalization: undefined,
            deactivatingMarkersIsBlocked: false,
            TramDataFeatureArray: undefined,
            activeTitle: "Krakowskie Przedmieście",
            GROUPS: GROUPS
        };
    },

    created() {
        window.addEventListener("resize", e => {
            this.windowResized(e);
        });
    },

    async mounted() {
        try {
            this.google = await gmapsInit();
            this.map = new this.google.maps.Map(this.$refs.map, {
                center: this.center,
                zoom: this.zoom,
                zoomControl: true,
                mapTypeControl: false,
                scaleControl: false,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: false
            });
            this.map.setOptions({
                styles: this.styles
            });
            this.bounds = new this.google.maps.LatLngBounds();
            this.localizations = this.backendData.media_map_featured_localizations;

            all([getLocalizations(), getPublicTransport()]).then(
                spread((...responses) => {
                    const projects = responses[0];
                    const geojson = responses[1];
                    this.projects = projects.data;
                    this.setupFilters();
                    this.setupCategories();
                    this.redrawMarkers();
                    const [firstMarker] = this.markers;
                    if (firstMarker) {
                        this.moveTo(firstMarker.getPosition());
                        this.adjustZoom();
                        this.initMoveEvents();
                        this.setOffset();
                    }
                    this.geojson = geojson.data;
                    this.redrawPublicTransport(false);
                    this.loading = false;
                    const [
                        firstProject
                    ] = this.backendData.media_map_featured_localizations;
                    if (firstProject) {
                        this.changeActiveLocalization(firstProject);
                    }
                })
            );
        } catch (error) {
            console.error(error);
        }
    },

    methods: {
        markerClicked(data, Marker, projectData) {
            if (!this.deactivatingMarkersIsBlocked) {
                this.deactivateMarker();
            }
            Marker.setIcon(getActiveMarker());
            this.moveTo(data.position);
            this.pristine = false;
            const countMedia = this.filters
                .filter(_filter => _filter.type === data.title)
                .map(_filter => _filter.count)[0];
            const slug = slugify(data.title, {
                lower: true
            });

            this.current = {
                img: `/content/themes/warexpo/assets/images/map/${slug}.jpg`,
                title: data.title,
                address: data.address,
                lat: data.position.lat,
                lng: data.position.lng,
                countMedia: countMedia,
                countReceivers: 999999, // TODO
                slug: slug
            };
            if (!this.deactivatingMarkersIsBlocked) {
                this.deactivateMarker();
            }

            this.active = {
                data: projectData,
                Marker
            };
            this.activeLocalization = projectData;
            this.scrollToMap();
        },

        redrawMarkers() {
            this.markers.forEach(marker => marker.setMap(null));
            this.markers = [];

            const activeCategories = this.categories
                .filter(c => c.active)
                .map(c => c.name);

            // const activeFilters = this.filters.filter(f => f.active).map(f => f.type);
            this.markers = this.projects
                .filter(p => {
                    return (
                        p.lat && p.lng && activeCategories.includes(p.category)
                    );
                })
                .map(p => {
                    p.lat = parseFloat(p.lat);
                    p.lng = parseFloat(p.lng);
                    let marker = this.drawMarker(
                        {
                            position: {
                                lat: p.lat,

                                lng: p.lng
                            },
                            icon: getMarker(),
                            title: p.type,
                            address: p.name
                        },
                        p
                    );

                    return marker;
                });
        },

        drawMarker(data, projectData) {
            const Marker = new this.google.maps.Marker({
                ...data,
                map: this.map
            });
            Marker.addListener("click", () =>
                this.markerClicked(data, Marker, projectData)
            );
            this.bounds.extend(data.position);
            return Marker;
        },

        moveTo(position) {
            this.map.panTo(position);
            this.map.panBy(this.mapOffset, 0); // TODO: offset related to zoom (?)
            this.center = position;
        },

        adjustZoom() {
            this.map.fitBounds(this.bounds);
        },

        windowResized(e) {
            this.setOffset();
        },

        setOffset() {
            this.checkMobile();
            this.mapOffset = this.mobile ? 0 : -450;
        },

        scrollToMap() {
            if (!this.mobile) {
                return;
            }
            const offset = -200;
            const y =
                this.$refs["map"].getBoundingClientRect().top +
                window.pageYOffset +
                offset;

            window.scrollTo({ top: y, behavior: "smooth" });
        },

        checkMobile() {
            this.mobile = window.innerWidth <= 992;
        },

        initMoveEvents() {
            this.map.addListener("dragstart", e => {
                this.isMapDragged = true;
                // this.deactivateMarker();
            });

            this.map.addListener("dragend", e => {
                this.isMapDragged = false;
            });
        },

        setupFilters() {
            const types = [...new Set(this.projects.map(media => media.type))];

            this.filters = types.map(type => {
                const count = this.projects.filter(media => media.type === type)
                    .length;

                return {
                    type,
                    count,
                    active: true
                };
            });
        },

        setupCategories() {
            const categories = [
                ...new Set(this.projects.map(media => media.category))
            ];

            this.categories = categories.map(category => {
                return {
                    name: category,
                    active: true
                };
            });
        },

        deactivateMarker() {
            if (this.active && this.active.Marker) {
                this.active.Marker.setIcon(getDefaultMarker());
            }
            this.active = null;
        },

        changeActiveLocalization(localization) {
            if (localization.lat && localization.lng) {
                const lat = parseFloat(localization.lat);
                const lng = parseFloat(localization.lng);
                const Marker = this.markers.filter(
                    m =>
                        m.getPosition().lat() === lat &&
                        m.getPosition().lng() === lng
                )[0];
                if (Marker) {
                    this.google.maps.event.trigger(Marker, "click", {});
                }
            } else {
                // TODO: it's extra-quick feature before release. Refactor before internationalization.
                this.deactivatingMarkersIsBlocked = true;
                if (GROUPS[localization.title]) {
                    let lats = [];
                    let lngs = [];
                    this.redrawMarkers();
                    this.bounds = new this.google.maps.LatLngBounds();
                    GROUPS[localization.title].forEach(([_lat, _lng]) => {
                        const lat = parseFloat(_lat);
                        const lng = parseFloat(_lng);
                        const Marker = this.markers.filter(
                            m =>
                                m.getPosition().lat() === lat &&
                                m.getPosition().lng() === lng
                        )[0];
                        if (Marker) {
                            this.google.maps.event.trigger(Marker, "click", {});
                            this.bounds.extend({
                                lat: Marker.getPosition().lat(),
                                lng: Marker.getPosition().lng()
                            });
                        }
                        this.adjustZoom();
                        lats.push(lat);
                        lngs.push(lng);
                    });
                    this.moveTo({
                        lat: getAveragePosition(lats),
                        lng: getAveragePosition(lngs)
                    });
                } else if (this.isPublicTransport(localization)) {
                    this.redrawMarkers();
                } else {
                    this.bounds = new this.google.maps.LatLngBounds();
                    this.redrawMarkers();
                    this.adjustZoom();
                    this.map.setZoom(this.zoom);
                }
                this.deactivatingMarkersIsBlocked = false;
                this.setOffset();
                this.redrawPublicTransport(
                    this.isPublicTransport(localization)
                );
            }

            this.activeTitle = localization.title;
        },

        redrawPublicTransport(visible) {
            if (!this.TramDataFeatureArray) {
                this.TramDataFeatureArray = this.map.data.addGeoJson(
                    this.geojson
                );
            }
            this.map.data.setStyle({
                visible: visible,
                strokeColor: "#FDB913",
                strokeWeight: 1
            });
        },

        isPublicTransport(localization) {
            return localization.title === "Komunikacja miejska";
        }
    },
    computed: {
        title() {
            return this.backendData.homepage_localizations_title;
        },

        subtitle() {
            return this.backendData.homepage_localizations_subtitle;
        },

        length() {
            if (this.projects) {
                return this.projects.length;
            }
            return 0;
        },

        backendData() {
            return warexpoHomepageMap;
        },

        resultText() {
            return this.translations.found_x_projects_near
                ? this.translations.found_x_projects_near.replace(
                      "{{x}}",
                      this.length
                  )
                : `Znaleziono ${this.length} realizacji w pobliżu`;
        },

        cta() {
            const isVisible =
                this.backendData.homepage_localizations_button_label &&
                this.backendData.homepage_localizations_button_url &&
                this.activeLocalization;

            return {
                isVisible,
                url: !isVisible
                    ? null
                    : this.backendData.homepage_localizations_button_url +
                      `?lat=${this.activeLocalization.lat}&lng=${
                          this.activeLocalization.lng
                      }`,
                label: this.backendData.homepage_localizations_button_label
            };
        }
    },

    watch: {
        mapOffset() {
            if (this.map) {
                this.moveTo(this.center);
            }
        }
    }
};
</script>

<style scoped lang="scss">
@import "./assets/styles/base/variables";

.map {
    width: 100%;
    height: 720px;
}

.controls {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;

    @media (min-width: map-get($grid-breakpoints, "lg")) {
        flex-wrap: nowrap;
    }
}

.filters {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-evenly;
    flex-wrap: wrap;
    margin-top: 30px;
    @media (min-width: map-get($grid-breakpoints, "lg")) {
        margin-top: 0;
        padding-left: 10px;
        justify-content: space-evenly;
    }
}

.selects {
    display: flex;
    width: 100%;
    padding-left: 23px;
}

.select--address {
    position: relative;
    background: #fff;
    padding-right: 60px;
    border-radius: 2px;
    width: 100%;

    .input--address {
        color: gray(0, 0.66);
        padding: 11px 22px 11px 56px;
        font-family: inherit;
        border: none;
        width: 100%;
        background-image: url("data:image/svg+xml;charset=utf8,%3C?xml version='1.0' encoding='UTF-8'?%3E%3Csvg width='18px' height='18px' viewBox='0 0 18 18' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EDEE48380-3922-494C-9046-CC482C7DBDA9%3C/title%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='design_Mapa_nośników' transform='translate(-551.000000, -308.000000)' fill='%23B2B2B2'%3E%3Cg id='google_maps_search' transform='translate(310.000000, 293.000000)'%3E%3Cg id='ic_search_24px' transform='translate(241.000000, 15.000000)'%3E%3Cpath d='M12.5,11 L11.71,11 L11.43,10.73 C12.41,9.59 13,8.11 13,6.5 C13,2.91 10.09,0 6.5,0 C2.91,0 0,2.91 0,6.5 C0,10.09 2.91,13 6.5,13 C8.11,13 9.59,12.41 10.73,11.43 L11,11.71 L11,12.5 L16,17.49 L17.49,16 L12.5,11 L12.5,11 Z M6.5,11 C4.01,11 2,8.99 2,6.5 C2,4.01 4.01,2 6.5,2 C8.99,2 11,4.01 11,6.5 C11,8.99 8.99,11 6.5,11 L6.5,11 Z' id='Shape'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        background-position: center right;
        background-repeat: no-repeat;
        border-radius: 2px;
        height: 50px;

        &:focus {
            border: 0;
            outline: none;
        }
    }
}

.directions-icon {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 17px 0 12px;

    &:before {
        content: "";
        position: absolute;
        width: 1px;
        height: 28px;
        left: 0;
        background: #ddd;
    }
}

.hamburger-icon {
    position: absolute;
    height: 100%;
    left: 19px;
}

.media-map {
    position: relative;

    @media (min-width: map-get($grid-breakpoints, "lg")) {
        height: 720px;
    }
}

.box-wrapper {
    width: 100%;
    height: 100%;
    z-index: 1;

    @media (min-width: map-get($grid-breakpoints, "lg")) {
        width: 629px;
        height: auto;
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        margin: 0 9%;
        display: flex;
        align-items: center;
    }
}

.box {
    background-color: $primary-navy-blue;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 46px 70px 60px 60px;
}

.title {
    color: $white;
    font-size: 28px;
    font-weight: bold;
    letter-spacing: 0;
    line-height: 59px;
    margin-left: 21px;
    margin-bottom: 15px;
    @media (min-width: map-get($grid-breakpoints, "lg")) {
        font-size: 40px;
    }
    @media (min-width: map-get($grid-breakpoints, "xl")) {
        font-size: 50px;
    }
}

.subtitle {
    color: $white;
    font-size: 14px;
    letter-spacing: 0;
    line-height: 24px;
    opacity: 0.8;
    margin-bottom: 25px;
    margin-left: 24px;
    font-weight: 500;
    @media (min-width: map-get($grid-breakpoints, "xl")) {
        font-size: 18px;
    }
}

.cta {
    width: 196px;
    height: 42px;
    color: $white;
    background-color: $warexpo-blue;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 20px;
    text-align: center;
    display: flex;
    margin: auto;
    justify-content: center;
    align-items: center;

    @media (min-width: map-get($grid-breakpoints, "md")) {
        margin: unset;
    }
    @media (min-width: map-get($grid-breakpoints, "lg")) {
        width: 175px;
        font-size: 14px;
        padding: 8px 20px;
        margin-bottom: 15px;
    }
    @media (min-width: map-get($grid-breakpoints, "xl")) {
        width: 196px;
        font-size: 16px;
        padding: 10px 25px;
    }

    &:hover {
        color: $white;
        background-color: #0080ca;
    }
}

.featured-localizations {
    display: block;

    @media (min-width: map-get($grid-breakpoints, "md")) {
        display: flex;
    }
}

.loader-wrapper {
    position: absolute;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}
</style>
